import React, { useEffect, useRef, useState } from 'react';
import {
    Box, Grid, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, Stack
} from '@mui/material';
import styled from '@emotion/styled';
import { useHasPermission } from '../utils/permissionUtils';
import { useDispatch, useSelector } from 'react-redux';
import { addTimeOff, getCurrentLeave, getTimeOffOverlaps } from '../redux/ducks/timeoff';
import { showAlert } from '../redux/slices/alerts';
import { triggerRefresh } from '../redux/slices/refreshTriggers';
import AppDatePicker from './AppDatePicker';
import { formatDayShort } from '../utils/dateUtils';

const TIME_OFF_TYPES = ['Vacation', 'Sick', 'Personal', 'Other'];

const BoxContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    marginTop: theme.spacing(2),
    height: '100%',
}));

const BoxHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1),
    flexWrap: 'wrap',
}));

const BoxTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
}));

const ButtonRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
}));

const CurrentLeaveBanner = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
    padding: theme.spacing(1.25, 1.5),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
}));

const BannerTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
}));

const BannerDescription = styled(Typography)({
    fontStyle: 'italic',
    opacity: 0.9,
});

const StatsGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
}));

const StatsColumn = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
}));

const ColumnHeader = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
}));

const ColumnTotal = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xl,
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
}));

const TypeRow = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
});

const TypeLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
}));

const TypeValue = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
}));

const ColumnSubLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
}));

const RequestStack = styled(Stack)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const OverlapNotice = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
    border: `1px solid ${theme.palette.warning.main}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.25, 1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
}));

const OverlapTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
}));

const OverlapHelp = styled(Typography)({
    opacity: 0.9,
});

const OverlapList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.5),
}));

const OverlapEntry = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const OverlapEntryDates = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
}));

const OverlapEntryMeta = styled(Typography)({
    opacity: 0.9,
});

const calcDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
    const ms = end - start;
    if (ms < 0) return 0;
    return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
};

const formatRangeLabel = (start, end) => {
    if (!start || !end) return '';
    const s = formatDayShort(start);
    const e = formatDayShort(end);
    return s === e ? s : `${s} → ${e}`;
};

const OVERLAP_STATUS_LABELS = {
    Approved: 'Already approved',
    Pending: 'Waiting for approval',
};

const StatsColumnView = ({ label, bucket }) => {
    if (!bucket) {
        return (
            <StatsColumn>
                <ColumnHeader>{label}</ColumnHeader>
                <ColumnTotal>0</ColumnTotal>
            </StatsColumn>
        );
    }
    const entries = TIME_OFF_TYPES.filter((t) => (bucket.byType?.[t] || 0) > 0);
    return (
        <StatsColumn>
            <ColumnHeader>{label} ({bucket.year})</ColumnHeader>
            <ColumnTotal>{bucket.total || 0}</ColumnTotal>
            <ColumnSubLabel variant="caption">days taken</ColumnSubLabel>
            {entries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No time off recorded</Typography>
            ) : entries.map((t) => (
                <TypeRow key={t}>
                    <TypeLabel variant="body2">{t}</TypeLabel>
                    <TypeValue variant="body2">{bucket.byType[t]}</TypeValue>
                </TypeRow>
            ))}
        </StatsColumn>
    );
};

const ProfileTimeOffBox = ({ title = "", data, userId }) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const isSelf = userId === useSelector((state) => state.auth.userId);
    const canAddTimeOff = hasPermission('addTimeOff');
    const canReviewTimeOff = hasPermission('reviewTimeOff');
    const timeOffRefresh = useSelector((state) => state.refreshTriggers.timeOff);

    const [dialogMode, setDialogMode] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form, setForm] = useState({ startDate: '', endDate: '', type: 'Vacation', description: '' });
    const [currentLeave, setCurrentLeave] = useState(null);
    const [overlaps, setOverlaps] = useState([]);
    const overlapRequestRef = useRef(0);

    const fetchCurrent = () => {
        if (!userId) return;
        dispatch(getCurrentLeave(userId, (response) => {
            setCurrentLeave(response?.current || null);
        }));
    };

    useEffect(() => {
        fetchCurrent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, timeOffRefresh]);

    const thisYear = data?.thisYear;
    const lastYear = data?.lastYear;

    const days = calcDays(form.startDate, form.endDate);
    const canSubmit = !!(userId && form.startDate && form.endDate && form.type && days > 0);

    useEffect(() => {
        if (dialogMode === null || !userId || !form.startDate || !form.endDate || days <= 0) {
            setOverlaps([]);
            return undefined;
        }
        const requestId = overlapRequestRef.current + 1;
        overlapRequestRef.current = requestId;
        const timer = setTimeout(() => {
            dispatch(getTimeOffOverlaps(userId, form.startDate, form.endDate, null, (response) => {
                if (overlapRequestRef.current !== requestId) return;
                setOverlaps(response?.overlaps || []);
            }));
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dialogMode, userId, form.startDate, form.endDate, days]);

    const openDialog = (mode) => {
        setForm({ startDate: '', endDate: '', type: 'Vacation', description: '' });
        setOverlaps([]);
        setDialogMode(mode);
    };

    const closeDialog = () => {
        if (!isSubmitting) setDialogMode(null);
    };

    const handleField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const handleSubmit = () => {
        if (!canSubmit) return;
        setIsSubmitting(true);
        dispatch(addTimeOff(userId, form.startDate, form.endDate, days, form.type, form.description, (response) => {
            setIsSubmitting(false);
            const failed = !response || response?.error;
            if (failed) return;
            const successMsg = dialogMode === 'mark' ? 'Time off recorded' : 'Time off request submitted';
            dispatch(showAlert({ severity: 'success', message: successMsg }));
            dispatch(triggerRefresh({ triggerName: 'timeOff' }));
            setDialogMode(null);
        }));
    };

    const showRequest = isSelf && (canAddTimeOff || canReviewTimeOff);
    const showMarkOut = !isSelf && canReviewTimeOff;

    return (
        <>
            <Grid item xs={12} md={4} lg={4}>
                <BoxContainer>
                    <BoxHeader>
                        <BoxTitle variant="h5">{title}</BoxTitle>
                        <ButtonRow>
                            {showRequest && (
                                <Button variant="outlined" color="primary" onClick={() => openDialog('request')}>
                                    Request Time Off
                                </Button>
                            )}
                            {showMarkOut && (
                                <Button variant="contained" color="primary" onClick={() => openDialog('mark')}>
                                    Mark as Out
                                </Button>
                            )}
                        </ButtonRow>
                    </BoxHeader>

                    {currentLeave && (
                        <CurrentLeaveBanner>
                            <BannerTitle variant="body2">
                                Out: {currentLeave.type} — {formatRangeLabel(currentLeave.startDate, currentLeave.endDate)}
                            </BannerTitle>
                            {currentLeave.description && (
                                <BannerDescription variant="body2">{currentLeave.description}</BannerDescription>
                            )}
                        </CurrentLeaveBanner>
                    )}

                    <StatsGrid>
                        <StatsColumnView label="This Year" bucket={thisYear} />
                        <StatsColumnView label="Last Year" bucket={lastYear} />
                    </StatsGrid>
                </BoxContainer>
            </Grid>

            <Dialog open={dialogMode !== null} onClose={closeDialog} fullWidth maxWidth="xs">
                <DialogTitle>{dialogMode === 'mark' ? 'Mark User as Out' : 'Request Time Off'}</DialogTitle>
                <DialogContent>
                    <RequestStack spacing={2}>
                        <AppDatePicker
                            label="Start Date"
                            value={form.startDate}
                            onChange={(v) => setForm((f) => ({ ...f, startDate: v }))}
                        />
                        <AppDatePicker
                            label="End Date"
                            value={form.endDate}
                            onChange={(v) => setForm((f) => ({ ...f, endDate: v }))}
                        />
                        {overlaps.length > 0 && (
                            <OverlapNotice>
                                <OverlapTitle variant="body2">
                                    {isSelf ? 'You already have time off on these dates' : 'This person already has time off on these dates'}
                                </OverlapTitle>
                                <OverlapHelp variant="body2">
                                    Nothing is blocked — you can still save this. Please make sure it is not the same time off being entered twice.
                                </OverlapHelp>
                                <OverlapList>
                                    {overlaps.map((entry) => (
                                        <OverlapEntry key={entry._id}>
                                            <OverlapEntryDates variant="body2">
                                                {entry.type} — {formatRangeLabel(entry.startDate, entry.endDate)}
                                            </OverlapEntryDates>
                                            <OverlapEntryMeta variant="caption">
                                                {OVERLAP_STATUS_LABELS[entry.status] || entry.status}
                                                {entry.days ? ` · ${entry.days} working day${entry.days === 1 ? '' : 's'}` : ''}
                                            </OverlapEntryMeta>
                                        </OverlapEntry>
                                    ))}
                                </OverlapList>
                            </OverlapNotice>
                        )}
                        <TextField
                            label="Type"
                            select
                            value={form.type}
                            onChange={handleField('type')}
                            fullWidth
                        >
                            {TIME_OFF_TYPES.map((t) => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Description"
                            multiline
                            minRows={3}
                            value={form.description}
                            onChange={handleField('description')}
                            fullWidth
                        />
                        <Typography variant="body2" color="text.secondary">
                            {days > 0 ? `${days} calendar day${days === 1 ? '' : 's'} (working-day total will be computed server-side)` : 'Pick a start and end date'}
                        </Typography>
                    </RequestStack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={isSubmitting}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={!canSubmit || isSubmitting} variant="contained" color="primary">
                        {isSubmitting ? 'Submitting…' : (dialogMode === 'mark' ? 'Save' : 'Submit Request')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ProfileTimeOffBox;
